import { utf16Length, IsoTimestampSchema } from "./string-validation"
import { z } from "zod"

import { JourneyDefinitionIdSchema } from "./journey-definitions"
import { LabAiDateTypeSchema, LabAiErrorCodeSchema, LabAiProviderSchema, LabAiReasonSchema } from "./lab-ai"

export const LabStatusSchema = z
	.enum([
		"not_started",
		"ordered",
		"kit_shipped",
		"kit_delivered",
		"sample_in_transit",
		"sample_delivered",
		"sample_received",
		"results_pending",
		"results_approved",
		"results_rejected",
		"failed",
	])
	.meta({ id: "LabStatus" })

export type LabStatus = z.infer<typeof LabStatusSchema>

export const IntegrationCaseStatusSchema = z
	.enum([
		"not_started",
		"patient_failed",
		"patient_created",
		"lab_failed",
		"lab_ordered",
		"lab_results_uploaded",
		"product_order_failed",
		"product_ordered",
		"form_submission_failed",
		"forms_submitted",
	])
	.meta({ id: "IntegrationCaseStatus" })

export type IntegrationCaseStatus = z.infer<typeof IntegrationCaseStatusSchema>

export const IntegrationRequestStatusSchema = z
	.enum(["pending", "succeeded", "failed"])
	.meta({ id: "IntegrationRequestStatus" })

export type IntegrationRequestStatus = z.infer<typeof IntegrationRequestStatusSchema>

export const IntegrationAdapterKeySchema = z
	.enum(["gen_health", "spot"])
	.meta({ id: "IntegrationAdapterKey" })

export type IntegrationAdapterKey = z.infer<typeof IntegrationAdapterKeySchema>

export const LabTimelineEventSchema = z
	.object({
		status: LabStatusSchema,
		recordedAt: IsoTimestampSchema,
		vendor: z.string().nullable().optional(),
	})
	.meta({ id: "LabTimelineEvent" })

export type LabTimelineEvent = z.infer<typeof LabTimelineEventSchema>

export const LabLatestRequestSchema = z
	.object({
		id: z.string(),
		requestType: z.enum(["create_lab_order", "upload_lab_results"]),
		status: IntegrationRequestStatusSchema,
		attemptNumber: z.number().int().positive(),
		httpStatus: z.number().int().nullable(),
		startedAt: IsoTimestampSchema,
		completedAt: IsoTimestampSchema.nullable(),
		externalOrderId: z.string().nullable().optional(),
	})
	.meta({ id: "LabLatestRequest" })

export type LabLatestRequest = z.infer<typeof LabLatestRequestSchema>

export const LabOrderSummarySchema = z
	.object({
		id: z.string(),
		currentStatus: z.string(),
		displayStatus: z.string(),
		journeyDefinitionId: JourneyDefinitionIdSchema,
		journeyDefinitionVersion: z.number().int().positive(),
	})
	.meta({ id: "LabOrderSummary" })

export type LabOrderSummary = z.infer<typeof LabOrderSummarySchema>

export const LabIntegrationSchema = z
	.object({
		adapterKey: IntegrationAdapterKeySchema.nullable(),
		caseStatus: IntegrationCaseStatusSchema.nullable(),
		externalPatientId: z.string().nullable().optional(),
		externalLabOrderId: z.string().nullable().optional(),
		externalLabOrderIds: z.array(z.string()).optional(),
		lastRequestAt: IsoTimestampSchema.nullable(),
		lastSuccessAt: IsoTimestampSchema.nullable(),
		lastFailureAt: IsoTimestampSchema.nullable(),
		lastHttpStatus: z.number().int().nullable(),
		lastErrorMessage: z.string().nullable().optional(),
		lastErrorCode: z.string().nullable(),
	})
	.meta({ id: "LabIntegration" })

export type LabIntegration = z.infer<typeof LabIntegrationSchema>

export const LabUploadDocumentStatusSchema = z
	.enum([
		"uploaded",
		"analysis_failed",
		"eligibility_failed",
		"human_review_requested",
		"submitted_to_gen_health",
		"accepted_for_review",
		"rejected",
		"superseded",
		"gen_health_failed",
	])
	.meta({ id: "LabUploadDocumentStatus" })

export type LabUploadDocumentStatus = z.infer<typeof LabUploadDocumentStatusSchema>

export const LabUploadEligibilityResultSchema = z
	.enum(["eligible", "ineligible", "requires_human_review", "analysis_failed"])
	.meta({ id: "LabUploadEligibilityResult" })

export type LabUploadEligibilityResult = z.infer<typeof LabUploadEligibilityResultSchema>

export const LabUploadDetectedDateSchema = z
	.object({
		normalizedDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
		dateType: LabAiDateTypeSchema,
		confidence: z.number().min(0).max(1),
	})
	.meta({ id: "LabUploadDetectedDate" })

export type LabUploadDetectedDate = z.infer<typeof LabUploadDetectedDateSchema>

export const LabUploadAiAnalysisSchema = z
	.object({
		status: z.enum(["not_started", "succeeded", "failed"]),
		eligibilityResult: LabUploadEligibilityResultSchema.nullable(),
		provider: LabAiProviderSchema.nullable(),
		modelId: z.string().nullable(),
		promptVersion: z.string().nullable(),
		selectedValidityDate: z
			.string()
			.regex(/^\d{4}-\d{2}-\d{2}$/)
			.nullable(),
		selectedDateType: LabAiDateTypeSchema.nullable(),
		selectedConfidenceBps: z.number().int().min(0).max(10000).nullable(),
		selectedReason: LabAiReasonSchema.nullable(),
		allDatesFound: z.array(LabUploadDetectedDateSchema),
		requiresHumanReview: z.boolean().nullable(),
		eligibilityCutoffDate: z
			.string()
			.regex(/^\d{4}-\d{2}-\d{2}$/)
			.nullable(),
		analyzedAt: IsoTimestampSchema.nullable(),
		errorCode: LabAiErrorCodeSchema.nullable(),
		supportReviewRequestedAt: IsoTimestampSchema.nullable(),
		supportReviewTicketId: z.uuid().nullable(),
	})
	.meta({ id: "LabUploadAiAnalysis" })

export type LabUploadAiAnalysis = z.infer<typeof LabUploadAiAnalysisSchema>

export const GenHealthLabResultDocumentSchema = z
	.object({
		externalDocumentId: z.string().min(1),
		externalPatientId: z.string().min(1),
		partnerPatientId: z.string().nullable(),
		documentType: z.string().min(1),
		documentName: z.string().min(1),
		submittedAt: IsoTimestampSchema,
	})
	.meta({ id: "GenHealthLabResultDocument" })

export type GenHealthLabResultDocument = z.infer<typeof GenHealthLabResultDocumentSchema>

export const LabUploadDocumentSchema = z
	.object({
		id: z.uuid(),
		orderId: z.uuid(),
		labId: z.uuid(),
		status: LabUploadDocumentStatusSchema,
		originalFileName: z.string().min(1),
		contentType: z.literal("application/pdf"),
		sizeBytes: z.number().int().positive(),
		sha256: z.string().apply(utf16Length("min", 64)).apply(utf16Length("max", 64)),
		createdAt: IsoTimestampSchema,
		submittedToGenHealthAt: IsoTimestampSchema.nullable(),
		acceptedForReviewAt: IsoTimestampSchema.nullable(),
		rejectionReasonCode: z.string().nullable(),
		rejectionReason: z.string().nullable(),
		rejectedAt: IsoTimestampSchema.nullable(),
		aiAnalysis: LabUploadAiAnalysisSchema.optional(),
		genHealthDocument: GenHealthLabResultDocumentSchema.nullable(),
	})
	.meta({ id: "LabUploadDocument" })

export type LabUploadDocument = z.infer<typeof LabUploadDocumentSchema>

export const LabSchema = z
	.object({
		id: z.string(),
		orderId: z.string(),
		patientId: z.string().optional(),
		patientName: z.string().optional(),
		status: LabStatusSchema,
		vendor: z.string().nullable(),
		trackingUrl: z.string().nullable().optional(),
		vendorLabOrderId: z.string().nullable().optional(),
		vendorCaseStatus: IntegrationCaseStatusSchema.nullable(),
		orderedAt: IsoTimestampSchema.nullable(),
		lastEventAt: IsoTimestampSchema.nullable(),
		lastSuccessAt: IsoTimestampSchema.nullable(),
		lastFailureAt: IsoTimestampSchema.nullable(),
		daysWaiting: z.number().int().nullable(),
		hasFailures: z.boolean(),
		hasUploadedLabDocument: z.boolean().optional(),
	})
	.meta({ id: "Lab" })

export type Lab = z.infer<typeof LabSchema>

export const LabDetailSchema = z
	.object({
		id: z.string(),
		orderId: z.string(),
		patientId: z.string().optional(),
		patientName: z.string().optional(),
		status: LabStatusSchema,
		vendor: z.string().nullable(),
		trackingUrl: z.string().nullable().optional(),
		vendorLabOrderId: z.string().nullable().optional(),
		orderedAt: IsoTimestampSchema.nullable(),
		lastEventAt: IsoTimestampSchema.nullable(),
		daysWaiting: z.number().int().nullable(),
		integration: LabIntegrationSchema,
		timeline: z.array(LabTimelineEventSchema),
		latestRequest: LabLatestRequestSchema.nullable(),
		uploadedDocuments: z.array(LabUploadDocumentSchema).optional(),
		order: LabOrderSummarySchema,
	})
	.meta({ id: "LabDetail" })

export type LabDetail = z.infer<typeof LabDetailSchema>

export const LabReviewDecisionSchema = z.enum(["approve", "reject"]).meta({ id: "LabReviewDecision" })

export type LabReviewDecision = z.infer<typeof LabReviewDecisionSchema>

export const LabReviewRequestSchema = z
	.object({
		decision: LabReviewDecisionSchema,
		reason: z.string().optional(),
	})
	.meta({ id: "LabReviewRequest" })

export type LabReviewRequest = z.infer<typeof LabReviewRequestSchema>

export const LabFiltersSchema = z
	.object({
		search: z.string().trim().apply(utf16Length("max", 200)).optional(),
		status: LabStatusSchema.optional(),
		page: z.coerce.number().int().positive().default(1),
		limit: z.coerce.number().int().positive().max(100).default(50),
	})
	.meta({ id: "LabFilters" })

export type LabFilters = z.infer<typeof LabFiltersSchema>
